import { useState } from "react";
import type { FormEvent, Ref } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { usePlayerSuggestions } from "../../hooks/usePlayerSuggestions";
import "./playerPicker.css";

type Props = {
  // Chosen from the suggestions, or typed and submitted.
  onPick: (name: string) => void;
  // When set, each suggestion also gets a "me" button that calls this.
  onPickAsMe?: (name: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  inputRef?: Ref<HTMLInputElement>;
  // When set, the input gets a search icon and a submit button with this label.
  submitLabel?: string;
};

// Themed player-name input with autocomplete suggestions underneath.
const PlayerPicker = ({
  onPick,
  onPickAsMe,
  placeholder = "Player name (3 characters minimum)",
  ariaLabel = "Player name",
  inputRef,
  submitLabel
}: Props) => {
  const [text, setText] = useState("");
  const [options, setOptions] = usePlayerSuggestions(text);

  const choose = (name: string, callback: (name: string) => void) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setOptions([]);
    setText("");
    callback(trimmed);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const exact = options.find(
      (option) => option.toLowerCase() === text.trim().toLowerCase()
    );
    choose(exact ?? text, onPick);
  };

  const input = (
    <Form.Control
      ref={inputRef}
      type="text"
      className="player-picker-input"
      value={text}
      onChange={(event) => setText(event.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      autoComplete="off"
    />
  );

  return (
    <div className="player-picker">
      <Form onSubmit={handleSubmit} role="search">
        {submitLabel ? (
          <InputGroup>
            <InputGroup.Text className="player-picker-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="6.5" cy="6.5" r="4.5" />
                <path d="M10 10l4.5 4.5" />
              </svg>
            </InputGroup.Text>
            {input}
            <Button type="submit" variant="outline-light" disabled={!text.trim()}>
              {submitLabel}
            </Button>
          </InputGroup>
        ) : (
          input
        )}
      </Form>
      {options.length > 0 && (
        <ul className="player-picker-list">
          {options.map((name) => (
            <li key={name}>
              <button type="button" onClick={() => choose(name, onPick)}>
                {name}
              </button>
              {onPickAsMe && (
                <button
                  type="button"
                  className="player-picker-me"
                  title={`Set ${name} as my character`}
                  aria-label={`Set ${name} as my character`}
                  onClick={() => choose(name, onPickAsMe)}
                >
                  ☆ me
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlayerPicker;
